import { DuplicateButton } from '../components/DuplicateButton'
import { RemoveButton } from '../components/RemoveButton'
import { SplitButton } from '../components/SplitButton'
import { EditButton } from '../components/EditButton'
import { useSelector } from 'react-redux'
import { editorSelectors } from 'store/features/editor/selectors'
import { FrameExtractButton } from '../components/FrameExtractButton'
import { useResponsive } from 'hooks/useResponsive'
import {
  imageTypes,
  videoTypes,
  MOBILE_SIZE_BREAKPOINT,
  audioTypes
} from 'consts'
import MagnetButton from '../components/MagnetButton'
import { useActiveItems } from './use-active-items'
import VolumeControlButton from '../components/VolumeControlButton'
import AnimateButton from '../components/AnimateButton'
import PlaybackSpeedButton from '../components/PlaybackSpeedButton'
import { selectActiveAudio, selectActiveMedia } from 'store/selectors'
import { useMemo } from 'react'
import { MediaStatus } from 'types/media'

export interface IButtonData {
  key: string
  component: React.ReactElement
  isVisible: boolean
}

const hasVolumeForAllItems = <T,>(
  items: T[],
  getVolume: (item: T) => unknown
): boolean => items.every(item => getVolume(item) !== undefined)

export const useTimelineButtons = (): {
  primaryButtons: IButtonData[]
  secondaryButtons: IButtonData[]
} => {
  const isMobile = useResponsive(MOBILE_SIZE_BREAKPOINT)
  const isEditButtonVisible = useSelector(
    editorSelectors.isTimelineHeaderEditButtonVisible
  )
  const activeMediaItem = useSelector(selectActiveMedia)
  const activeAudioItem = useSelector(selectActiveAudio)
  const { activeMediaItems, activeAudioItems } = useActiveItems()

  const hasVolumeControl = useMemo(() => {
    if (!activeMediaItems.length && !activeAudioItems.length) {
      return false
    }
    const hasVolumeForMediaItems = activeMediaItems.length
      ? hasVolumeForAllItems(activeMediaItems, item => item.meta?.volume)
      : true
    const hasVolumeForAudioItems = activeAudioItems.length
      ? hasVolumeForAllItems(activeAudioItems, item => item.volume)
      : true
    return hasVolumeForMediaItems && hasVolumeForAudioItems
  }, [activeMediaItems, activeAudioItems])

  const isImageType = useMemo(
    () => Boolean(activeMediaItem && imageTypes.includes(activeMediaItem.type)),
    [activeMediaItem]
  )

  const isVideoType = useMemo(
    () => Boolean(activeMediaItem && videoTypes.includes(activeMediaItem.type)),
    [activeMediaItem]
  )

  const isAudioType = useMemo(
    () => Boolean(activeAudioItem && audioTypes.includes(activeAudioItem.type)),
    [activeAudioItem]
  )

  const isMediaItemGenerating = useMemo(() => {
    if (activeMediaItem) {
      return activeMediaItem.meta.status === MediaStatus.PENDING
    }
    return false
  }, [activeMediaItem])

  const isPlaybackSpeedButtonVisible = useMemo(
    () => (isVideoType && !isMediaItemGenerating) || isAudioType,
    [isVideoType, isAudioType, isMediaItemGenerating]
  )

  const playbackSpeedButton = useMemo(
    () => ({
      key: 'playbackSpeed',
      component: <PlaybackSpeedButton />,
      isVisible: isPlaybackSpeedButtonVisible
    }),
    [isPlaybackSpeedButtonVisible]
  )

  const frameExtractButton = useMemo(
    () => ({
      key: 'frameExtract',
      component: <FrameExtractButton />,
      isVisible: true
    }),
    []
  )

  const primary = useMemo(
    () => [
      {
        key: 'edit',
        component: <EditButton />,
        isVisible: isEditButtonVisible
      },
      {
        key: 'split',
        component: <SplitButton />,
        isVisible: true
      }
    ],
    [isEditButtonVisible]
  )

  const additionalButtons = useMemo(
    () => [
      {
        key: 'volume',
        isVisible: hasVolumeControl,
        component: <VolumeControlButton />
      },
      {
        key: 'animate',
        isVisible: isImageType,
        component: <AnimateButton />
      }
    ],
    [hasVolumeControl, isImageType]
  )

  const secondary = useMemo(
    () => [
      {
        key: 'delete',
        component: <RemoveButton />,
        isVisible: true
      },
      {
        key: 'duplicate',
        component: <DuplicateButton />,
        isVisible: true
      },
      {
        key: 'magnet',
        component: <MagnetButton />,
        isVisible: true
      }
    ],
    []
  )

  const webButtons = useMemo(
    () => [
      ...primary,
      frameExtractButton,
      ...secondary,
      ...additionalButtons,
      playbackSpeedButton
    ],
    [
      primary,
      secondary,
      additionalButtons,
      playbackSpeedButton,
      frameExtractButton
    ]
  )

  return isMobile
    ? {
        primaryButtons: [...primary, ...additionalButtons, playbackSpeedButton],
        secondaryButtons: [...secondary, frameExtractButton]
      }
    : {
        primaryButtons: webButtons,
        secondaryButtons: []
      }
}
